'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { Download, Archive, CheckCircle, FolderOpen, CloudUpload, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { createClient } from '@/lib/supabase/client';

interface Report {
  id: string;
  title: string;
  platform: string | null;
  format: string | null;
  tone: string | null;
  status: string | null;
  caption: string | null;
  image_url: string | null;
  created_at: string;
}

interface Bundle {
  id: string;
  name: string;
  platform: string;
  platformKey: string;
  reports: Report[];
  created: string;
  status: 'bereit' | 'exportiert' | 'ausstehend';
}

const platformBadge: Record<string, string> = {
  LI: 'platform-badge-li',
  IG: 'platform-badge-ig',
  X: 'platform-badge-x',
  FB: 'platform-badge-fb',
  TT: 'platform-badge-tt',
  BLOG: 'platform-badge-blog',
};

const platformLabel: Record<string, string> = {
  LinkedIn: 'LI',
  Instagram: 'IG',
  'X (Twitter)': 'X',
  Facebook: 'FB',
  TikTok: 'TT',
  Blog: 'BLOG',
};

function getPlatformKey(platform: string | null): string {
  if (!platform) return 'ALL';
  return platformLabel[platform] ?? platform.slice(0, 2).toUpperCase();
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function buildBundles(reports: Report[]): Bundle[] {
  const grouped: Record<string, Report[]> = {};

  reports.forEach((r) => {
    const key = r.platform ?? 'Alle Plattformen';
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(r);
  });

  // Also create an "All" bundle if there are reports from multiple platforms
  const platforms = Object.keys(grouped);
  if (platforms.length > 1) {
    grouped['Alle Plattformen'] = reports;
  }

  return Object.entries(grouped).map(([platform, reps], idx) => {
    const key = getPlatformKey(platform === 'Alle Plattformen' ? null : platform);
    const newest = reps.reduce((a, b) => (a.created_at > b.created_at ? a : b));
    return {
      id: `bundle-${idx}`,
      name: platform === 'Alle Plattformen' ? 'Vollständiges Export-Paket' : `${platform}-Paket`,
      platform,
      platformKey: key,
      reports: reps,
      created: formatDate(newest.created_at),
      status: reps.length > 0 ? 'bereit' : 'ausstehend',
    };
  });
}

function generateZipContent(bundle: Bundle): string {
  const lines: string[] = [];
  lines.push(`# ${bundle.name}`);
  lines.push(`Plattform: ${bundle.platform}`);
  lines.push(`Erstellt: ${bundle.created}`);
  lines.push(`Beiträge: ${bundle.reports.length}`);
  lines.push('');
  lines.push('---');
  lines.push('');

  bundle.reports.forEach((r, i) => {
    lines.push(`## ${i + 1}. ${r.title}`);
    if (r.platform) lines.push(`Plattform: ${r.platform}`);
    if (r.format) lines.push(`Format: ${r.format}`);
    if (r.tone) lines.push(`Ton: ${r.tone}`);
    if (r.status) lines.push(`Status: ${r.status}`);
    lines.push('');
    if (r.caption) {
      lines.push('### Caption');
      lines.push(r.caption);
      lines.push('');
    }
    if (r.image_url) {
      lines.push(`### Bild-URL`);
      lines.push(r.image_url);
      lines.push('');
    }
    lines.push('---');
    lines.push('');
  });

  return lines.join('\n');
}

function estimateSize(bundle: Bundle): string {
  const chars = generateZipContent(bundle).length;
  const kb = chars / 1024;
  return kb < 100 ? `${kb.toFixed(1)} KB` : `${(kb / 1024).toFixed(1)} MB`;
}

export default function ExportBundles() {
  const [bundles, setBundles] = useState<Bundle[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalReports, setTotalReports] = useState(0);
  const [downloading, setDownloading] = useState<string | null>(null);
  const [syncingDrive, setSyncingDrive] = useState<string | null>(null);
  const [driveLastSync, setDriveLastSync] = useState<string | null>(null);

  const loadReports = useCallback(async () => {
    setLoading(true);
    const supabase = createClient();
    const { data, error } = await supabase
      .from('reports')
      .select('id, title, platform, format, tone, status, caption, image_url, created_at')
      .order('created_at', { ascending: false });

    if (error) {
      toast.error('Fehler beim Laden der Berichte');
      setLoading(false);
      return;
    }

    const reports: Report[] = data ?? [];
    setTotalReports(reports.length);
    setBundles(buildBundles(reports));

    // Simulate last drive sync from most recent report
    if (reports.length > 0) {
      const latest = new Date(reports[0].created_at);
      const diffMs = Date.now() - latest.getTime();
      const diffH = Math.floor(diffMs / 3600000);
      setDriveLastSync(diffH < 1 ? 'Vor weniger als 1 Std.' : `Vor ${diffH} Std.`);
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  const handleDownloadZip = (bundle: Bundle) => {
    setDownloading(bundle.id);
    setTimeout(() => {
      const content = generateZipContent(bundle);
      const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${bundle.name.replace(/\s+/g, '-').toLowerCase()}.txt`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      setDownloading(null);
      toast.success(`${bundle.name} heruntergeladen`, {
        description: `${bundle.reports.length} Beiträge · ${estimateSize(bundle)}`,
      });
    }, 800);
  };

  const handleGoogleDriveSync = (bundle: Bundle) => {
    setSyncingDrive(bundle.id);
    // Simulated Drive sync — replace with real OAuth + Drive API call
    setTimeout(() => {
      setSyncingDrive(null);
      setDriveLastSync('Gerade eben');
      toast.success(`${bundle.name} zu Google Drive exportiert`, {
        description: `${bundle.reports.length} Dateien → /SEARCH4-DISTORTION/`,
      });
    }, 2000);
  };

  if (loading) {
    return (
      <div className="punk-card">
        <div className="flex items-center justify-between p-5 border-b border-border">
          <div>
            <p className="section-label">Export-Pakete</p>
            <div className="h-4 w-40 bg-surface-2 rounded animate-pulse mt-1" />
          </div>
        </div>
        <div className="divide-y divide-border">
          {[1, 2, 3].map((i) => (
            <div key={i} className="p-5 flex items-center gap-4">
              <div className="flex-1 space-y-2">
                <div className="h-4 w-48 bg-surface-2 rounded animate-pulse" />
                <div className="h-3 w-32 bg-surface-2 rounded animate-pulse" />
              </div>
              <div className="h-8 w-20 bg-surface-2 rounded animate-pulse" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (bundles.length === 0) {
    return (
      <div className="punk-card">
        <div className="p-5 border-b border-border">
          <p className="section-label">Export-Pakete</p>
          <h3 className="text-sm font-600 text-white mt-1">Keine Beiträge vorhanden</h3>
        </div>
        <div className="p-10 text-center">
          <Archive size={28} className="text-muted mx-auto mb-3" />
          <p className="text-sm font-500 text-white">Noch keine Beiträge</p>
          <p className="text-xs text-muted mt-1">Erstelle Beiträge im Content-Generator, um Export-Pakete zu erstellen.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="punk-card">
      <div className="flex items-center justify-between p-5 border-b border-border">
        <div>
          <p className="section-label">Export-Pakete</p>
          <h3 className="text-sm font-600 text-white mt-1">
            {bundles.length} Pakete · {totalReports} Beiträge gesamt
          </h3>
        </div>
        <button
          onClick={loadReports}
          className="btn-ghost text-xs"
        >
          <RefreshCw size={12} />
          Aktualisieren
        </button>
      </div>

      <div className="divide-y divide-border">
        {bundles.map((bundle) => (
          <div key={bundle.id} className="p-5 hover:bg-surface transition-colors group">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 mb-2">
                  <h4 className="text-sm font-600 text-white truncate">{bundle.name}</h4>
                  <span className={`status-badge border flex-shrink-0 bg-green-950/30 text-green-400 border-green-800/40`}>
                    <CheckCircle size={12} />
                    Bereit
                  </span>
                </div>
                <div className="flex items-center flex-wrap gap-2">
                  {bundle.platformKey !== 'ALL' ? (
                    <span className={`status-badge ${platformBadge[bundle.platformKey] ?? 'bg-surface-2 text-muted border-border'}`}>
                      {bundle.platformKey}
                    </span>
                  ) : (
                    <span className="status-badge bg-surface-2 text-muted border-border">ALL</span>
                  )}
                  <span className="text-muted">·</span>
                  <span className="mono text-xs text-muted">{bundle.reports.length} Beiträge</span>
                  <span className="text-muted">·</span>
                  <span className="mono text-xs text-muted">{estimateSize(bundle)}</span>
                  <span className="text-muted">·</span>
                  <span className="mono text-xs text-muted bg-surface border border-border px-1.5 py-0.5 rounded">TXT</span>
                </div>
              </div>

              <div className="flex items-center gap-2 flex-shrink-0">
                <span className="mono text-xs text-muted hidden group-hover:block">{bundle.created}</span>

                {/* Google Drive Export */}
                <button
                  onClick={() => handleGoogleDriveSync(bundle)}
                  disabled={syncingDrive === bundle.id}
                  className="btn-ghost py-1.5 px-3 text-xs gap-1.5"
                  title="Zu Google Drive exportieren"
                >
                  {syncingDrive === bundle.id ? (
                    <>
                      <div className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin" />
                      Drive…
                    </>
                  ) : (
                    <>
                      <CloudUpload size={12} />
                      Drive
                    </>
                  )}
                </button>

                {/* ZIP Download */}
                <button
                  onClick={() => handleDownloadZip(bundle)}
                  disabled={downloading === bundle.id}
                  className="btn-primary py-1.5 px-3 text-xs gap-1.5"
                >
                  {downloading === bundle.id ? (
                    <>
                      <div className="w-3 h-3 border border-black border-t-transparent rounded-full animate-spin" />
                      Lädt…
                    </>
                  ) : (
                    <>
                      <Download size={12} />
                      ZIP
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Google Drive Status Footer */}
      <div className="p-4 border-t border-border bg-surface/50">
        <div className="flex items-center gap-3">
          <div className="w-7 h-7 rounded flex items-center justify-center" style={{ background: '#34A85320', border: '1px solid #34A85340' }}>
            <FolderOpen size={14} style={{ color: '#34A853' }} />
          </div>
          <div className="flex-1">
            <p className="text-xs font-600 text-white">Google Drive Sync</p>
            <p className="text-xs text-muted mono">
              /SEARCH4-DISTORTION/ · {driveLastSync ? `Zuletzt: ${driveLastSync}` : 'Noch nicht synchronisiert'}
            </p>
          </div>
          <div className="flex items-center gap-1.5">
            <div className={`w-1.5 h-1.5 rounded-full ${driveLastSync ? 'bg-green-400' : 'bg-muted'}`} />
            <span className={`text-xs mono ${driveLastSync ? 'text-green-400' : 'text-muted'}`}>
              {driveLastSync ? 'Verbunden' : 'Nicht verbunden'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}